import React, {useEffect, useMemo, useState} from 'react';
import DropdownField from './DropdownField';
import {
    DEFAULT_RELATIONSHIP_TYPE,
    RELATIONSHIP_TYPE_PRESETS,
    resolveRelationshipTypeValue,
    splitRelationshipTypeValue,
} from '../constants/relationshipTypes';

interface RelationshipTypeSelectProps {
    value?: string | null;
    onChange: (value: string) => void;
    disabled?: boolean;
    label?: string;
    className?: string;
}

function RelationshipTypeSelect({
    value,
    onChange,
    disabled = false,
    label = 'Relationship',
    className,
}: RelationshipTypeSelectProps) {
    const parsed = useMemo(() => splitRelationshipTypeValue(value), [value]);
    const [presetValue, setPresetValue] = useState(parsed.presetValue);
    const [customValue, setCustomValue] = useState(parsed.customValue);

    useEffect(() => {
        setPresetValue(parsed.presetValue);
        setCustomValue(parsed.customValue);
    }, [parsed.presetValue, parsed.customValue]);

    const presetOptions = useMemo(
        () => RELATIONSHIP_TYPE_PRESETS.map((preset) => ({
            value: preset.value,
            label: preset.label,
        })),
        [],
    );

    const emitChange = (nextPreset: string, nextCustom: string) => {
        onChange(resolveRelationshipTypeValue(nextPreset, nextCustom));
    };

    const handlePresetChange = (selection: { value: string }) => {
        const nextPreset = selection.value ?? DEFAULT_RELATIONSHIP_TYPE;
        setPresetValue(nextPreset);
        emitChange(nextPreset, customValue);
    };

    return (
        <div className={className}>
            <DropdownField
                label={label}
                value={presetValue}
                options={presetOptions}
                placeholder="Select relationship"
                disabled={disabled}
                onChange={handlePresetChange}
            />
            {presetValue === 'other' && (
                <div className="mt-2">
                    <label className="font-sm font-bold mb-1 d-block">Custom relationship</label>
                    <input
                        type="text"
                        className="form-control"
                        value={customValue}
                        disabled={disabled}
                        maxLength={50}
                        placeholder="e.g. Foster parent"
                        onChange={(event) => {
                            const nextCustom = event.target.value;
                            setCustomValue(nextCustom);
                            emitChange('other', nextCustom);
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export default RelationshipTypeSelect;
